import React from "react";
import { useState, useEffect, useRef, useCallback } from "react";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { X, Plus } from "lucide-react";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";

const PREDEFINED_TAGS = [
  { key: "influencer", label: "Influencer" },
  { key: "product", label: "Product" },
  { key: "offer", label: "Offer" },
  { key: "concept", label: "Concept" },
] as const;

const MAX_CUSTOM_TAGS = 5;

interface CreativeTagEditorProps {
  creativeId: string;
  initialTags: Record<string, string> | null;
  onTagsUpdated?: (tags: Record<string, string>) => void;
}

export function CreativeTagEditor({ creativeId, initialTags, onTagsUpdated }: CreativeTagEditorProps) {
  const { toast } = useToast();
  const [tags, setTags] = useState<Record<string, string>>({});
  const [customKeys, setCustomKeys] = useState<string[]>([]);
  const [newCustomKey, setNewCustomKey] = useState("");
  const [showAddCustom, setShowAddCustom] = useState(false);
  const saveTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // Init from props
  useEffect(() => {
    const t = initialTags || {};
    setTags(t);
    // Extract custom keys (not predefined)
    const predefinedKeys = PREDEFINED_TAGS.map(p => p.key as string);
    const custom = Object.keys(t).filter(k => !predefinedKeys.includes(k));
    setCustomKeys(custom);
  }, [initialTags]);

  const saveTags = useCallback((updatedTags: Record<string, string>) => {
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(async () => {
      // Filter out empty values
      const cleaned: Record<string, string> = {};
      for (const [k, v] of Object.entries(updatedTags)) {
        if (v && v.trim()) cleaned[k] = v.trim();
      }

      const { error } = await supabase
        .from("creative_metadata")
        .update({ tags: cleaned })
        .eq("id", creativeId);

      if (error) {
        toast({ title: "Failed to save tags", variant: "destructive" });
      } else {
        onTagsUpdated?.(cleaned);
      }
    }, 500);
  }, [creativeId, toast, onTagsUpdated]);

  const updateTag = (key: string, value: string) => {
    const updated = { ...tags, [key]: value };
    setTags(updated);
    saveTags(updated);
  };

  const clearTag = (key: string) => {
    const updated = { ...tags };
    delete updated[key];
    setTags(updated);
    saveTags(updated);
  };

  const removeCustomTag = (key: string) => {
    clearTag(key);
    setCustomKeys(prev => prev.filter(k => k !== key));
  };

  const addCustomTag = () => {
    const key = newCustomKey.trim().toLowerCase().replace(/\s+/g, "_");
    if (!key) return;
    const allKeys = [...PREDEFINED_TAGS.map(p => p.key), ...customKeys];
    if (allKeys.includes(key)) {
      toast({ title: "Tag key already exists", variant: "destructive" });
      return;
    }
    setCustomKeys(prev => [...prev, key]);
    setNewCustomKey("");
    setShowAddCustom(false);
  };

  const existingCustomCount = customKeys.length;

  return (
    <div className="space-y-2">
      <p className="text-xs font-medium text-muted-foreground uppercase tracking-wider">Tags</p>
      
      {/* Predefined tags */}
      {PREDEFINED_TAGS.map(({ key, label }) => (
        <div key={key} className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground w-20 shrink-0">{label}</span>
          <div className="relative flex-1">
            <Input
              value={tags[key] || ""}
              onChange={(e) => updateTag(key, e.target.value)}
              onBlur={() => saveTags(tags)}
              placeholder={`Enter ${label.toLowerCase()}...`}
              className="h-7 text-xs bg-background/50 border-border/50 pr-7"
            />
            {tags[key] && (
              <button
                onClick={() => clearTag(key)}
                className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
              >
                <X className="h-3 w-3" />
              </button>
            )}
          </div>
        </div>
      ))}

      {/* Custom tags */}
      {customKeys.map(key => (
        <div key={key} className="flex items-center gap-2">
          <span className="text-xs text-muted-foreground w-20 shrink-0 truncate" title={key}>{key}</span>
          <div className="relative flex-1">
            <Input
              value={tags[key] || ""}
              onChange={(e) => updateTag(key, e.target.value)}
              onBlur={() => saveTags(tags)}
              placeholder={`Enter ${key}...`}
              className="h-7 text-xs bg-background/50 border-border/50 pr-7"
            />
            <button
              onClick={() => removeCustomTag(key)}
              className="absolute right-1.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
            >
              <X className="h-3 w-3" />
            </button>
          </div>
        </div>
      ))}

      {/* Add custom tag */}
      {existingCustomCount < MAX_CUSTOM_TAGS && (
        showAddCustom ? (
          <div className="flex items-center gap-2">
            <Input
              value={newCustomKey}
              onChange={(e) => setNewCustomKey(e.target.value)}
              onKeyDown={(e) => e.key === "Enter" && addCustomTag()}
              placeholder="Tag name..."
              className="h-7 text-xs flex-1 bg-background/50 border-border/50"
              autoFocus
            />
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={addCustomTag}>Add</Button>
            <Button size="sm" variant="ghost" className="h-7 px-2 text-xs" onClick={() => { setShowAddCustom(false); setNewCustomKey(""); }}>
              <X className="h-3 w-3" />
            </Button>
          </div>
        ) : (
          <button
            onClick={() => setShowAddCustom(true)}
            className="text-xs text-muted-foreground hover:text-foreground flex items-center gap-1"
          >
            <Plus className="h-3 w-3" /> Add custom tag
          </button>
        )
      )}
    </div>
  );
}
